// Bridges the host's `selection-changed` DOM CustomEvent into the central
// `WhiteboardBus.selection$` BehaviorSubject. One of four state-stream
// adapters whitelisted by the `single-publisher-invariant` ESLint rule
// (file suffix match).
//
// SelectionController dispatches a bubbling CustomEvent on the host element
// (the <o2d-whiteboard>) with `detail: { ids: ReadonlyArray<string> }`. The
// adapter listens via `host.addEventListener(...)` and forwards `detail.ids`
// to `bus.selection$`. `dispose()` removes the listener — MUST be called from
// the host's `disconnectedCallback` BEFORE `bus.destroy()` (TR9 / FC-6 ordering).

import {
    SELECTION_CHANGED_EVENT,
    type SelectionChangedDetail,
} from '../selection/selection.types.js';
import { objectId } from './branded-types.js';
import type { WhiteboardBus } from './whiteboard-bus.js';

export class SelectionBusAdapter {
    readonly #host: HTMLElement;
    readonly #listener: (event: Event) => void;

    constructor(host: HTMLElement, bus: WhiteboardBus) {
        this.#host = host;
        this.#listener = (event: Event): void => {
            const detail = (event as CustomEvent<SelectionChangedDetail>).detail;
            bus.selection$.next(detail.ids.map(objectId));
        };
        this.#host.addEventListener(SELECTION_CHANGED_EVENT, this.#listener);
    }

    /** Remove the host listener. Idempotent. */
    dispose(): void {
        this.#host.removeEventListener(SELECTION_CHANGED_EVENT, this.#listener);
    }
}
